import { useStorage } from "@vueuse/core";
import { watch, onMounted } from "vue";

const THEME_STORAGE_KEY = "ja-core-theme-selection";
const STYLE_TAG_ID = "ja-core-theme";

// Use import.meta.glob to load all theme CSS files as raw strings
const themes = import.meta.glob("../css/themes/*.css", {
  query: "?inline",
  import: "default",
  eager: true
});

export type ThemeName =
  | "blue"
  | "default"
  | "green"
  | "ja"
  | "orange"
  | "red"
  | "rose"
  | "violet"
  | "yellow";

export function useTheme(storageKey: string = THEME_STORAGE_KEY) {
  const selectedTheme = useStorage<ThemeName>(storageKey, "default");

  const availableThemes = Object.keys(themes)
    .map((path) => path.match(/\/([^\/]+)\.css$/)?.[1] as ThemeName)
    .filter(Boolean)
    // Sort so "default" is always first, then alphabetical
    .sort((a, b) => {
      if (a === "default") return -1;
      if (b === "default") return 1;
      return a.localeCompare(b);
    });

  function applyTheme(name: ThemeName) {
    const themeContent = themes[`../css/themes/${name}.css`] as string;
    if (!themeContent) return;

    document.documentElement.setAttribute("data-color-theme", name);

    let styleTag = document.getElementById(STYLE_TAG_ID) as HTMLStyleElement;
    if (!styleTag) {
      styleTag = document.createElement("style");
      styleTag.id = STYLE_TAG_ID;
      document.head.appendChild(styleTag);
    }

    styleTag.textContent = themeContent;
  }

  // Watch for changes and apply
  watch(selectedTheme, (newTheme) => {
    applyTheme(newTheme);
  });

  // Apply initial theme on mount
  onMounted(() => {
    applyTheme(selectedTheme.value);
  });

  return {
    theme: selectedTheme,
    setTheme: (name: ThemeName) => {
      selectedTheme.value = name;
    },
    availableThemes
  };
}
